import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Center, Container, Group, Pagination, Skeleton, Stack, Tabs, Text, TextInput } from '@mantine/core';
import PageTitle from '@/components/common/PageTitle';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconBriefcase, IconSearch } from '@tabler/icons-react';
import EmptyState from '@/components/common/EmptyState';
import DealsTable from '@/components/app/deals/DealsTable';
import { DEALS_PAGE_SIZE, useMyBargains } from '@/hooks/useDeals';
import { parseDealTab } from '@/lib/dealStatus';
import { useIsAgent } from '@/hooks/useRoles';
import { useDealSideTotals } from '@/hooks/useDealsOverview';
import { isServiceUnavailable } from '@/api/errors';
import { ROUTES } from '@/routes/paths';
import classes from '@/components/common/scrollTabs.module.css';

// «Сделки» — мои сделки. Вкладка в URL (?tab=) для возврата/шаринга.
// Продавец/покупатель: вкладки Продажи/Покупки (сторона сделки). Агент — посредник во всех сделках,
// стороны у него нет: вкладки по статусу Активные/Завершённые (единая выборка по его ИНН).
export default function DealsListPage() {
  const { t } = useTranslation();
  const isAgent = useIsAgent();
  const sideTotals = useDealSideTotals();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // Сторона (для продавца/покупателя) и статус (для агента) читаем из одного параметра ?tab=.
  const partyTab = parseDealTab(rawTab); // 'SALE' | 'PURCHASE'
  const agentCompleted = rawTab === 'completed';
  const tabValue = isAgent
    ? (agentCompleted ? 'completed' : 'active')
    : (partyTab === 'PURCHASE' ? 'purchase' : 'sale');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 350);

  // Смена вкладки/поиска возвращает на первую страницу (иначе можно «зависнуть» вне диапазона).
  useEffect(() => {
    setPage(1);
  }, [tabValue, debouncedSearch]);

  const { data, isLoading, isError, error, isFetching, refetch } = useMyBargains(
    isAgent
      ? { completed: agentCompleted, page, filter: debouncedSearch }
      : { orderType: partyTab, page, filter: debouncedSearch },
  );

  const bargains = useMemo(() => data?.bargains ?? [], [data]);
  // Бэкенд отдаёт общее количество, не число страниц — считаем сами.
  const totalPages = Math.ceil((data?.total ?? 0) / DEALS_PAGE_SIZE);

  function changeTab(value: string | null): void {
    if (!value) {
      return;
    }
    setSearchParams({ tab: value }, { replace: true, preventScrollReset: true });
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Stack gap={4}>
          <PageTitle>{t('app.deals.title')}</PageTitle>
          <Text size="sm" c="gray.8">
            {t('app.deals.subtitle')}
          </Text>
        </Stack>

        <Tabs value={tabValue} onChange={changeTab}>
          <Tabs.List className={classes.list}>
            {isAgent ? (
              <>
                <Tabs.Tab value="active">{t('app.deals.tabs.active')}</Tabs.Tab>
                <Tabs.Tab value="completed">{t('app.deals.tabs.completed')}</Tabs.Tab>
              </>
            ) : (
              <>
                <Tabs.Tab value="sale" rightSection={<TabCount count={debouncedSearch ? undefined : sideTotals.sale} />}>
                  {t('app.deals.tabs.sales')}
                </Tabs.Tab>
                <Tabs.Tab value="purchase" rightSection={<TabCount count={debouncedSearch ? undefined : sideTotals.purchase} />}>
                  {t('app.deals.tabs.purchases')}
                </Tabs.Tab>
              </>
            )}
          </Tabs.List>
        </Tabs>

        <TextInput
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={t('app.deals.searchPlaceholder')}
          leftSection={<IconSearch size={16} />}
          maw={420}
        />

        {isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
            <Group justify="space-between" wrap="nowrap">
              <Text>{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
              <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </Alert>
        ) : isLoading ? (
          <Stack gap="sm">
            <Skeleton h={150} />
            <Skeleton h={150} />
          </Stack>
        ) : bargains.length === 0 ? (
          <Card withBorder padding="lg">
            <EmptyState
              decorated={false}
              icon={<IconBriefcase size={30} />}
              title={debouncedSearch ? t('app.deals.nothingFound') : t('app.deals.empty')}
              description={
                debouncedSearch
                  ? undefined
                  : isAgent
                    ? t(agentCompleted ? 'app.deals.emptyAgentCompletedHint' : 'app.deals.emptyAgentActiveHint')
                    : t(partyTab === 'SALE' ? 'app.deals.emptySaleHint' : 'app.deals.emptyPurchaseHint')
              }
              action={
                debouncedSearch ? (
                  <Button variant="subtle" onClick={() => setSearch('')}>
                    {t('app.deals.resetSearch')}
                  </Button>
                ) : isAgent ? undefined : (
                  <Button component={Link} to={partyTab === 'SALE' ? ROUTES.APP_SELL : ROUTES.APP_BUY}>
                    {t(partyTab === 'SALE' ? 'app.deals.goToSell' : 'app.deals.goToBuy')}
                  </Button>
                )
              }
            />
          </Card>
        ) : (
          <Stack gap="md" className="app-fade-in" style={{ opacity: isFetching ? 0.7 : 1, transition: 'opacity 120ms' }}>
            <DealsTable deals={bargains} orderType={isAgent ? undefined : partyTab} isAgent={isAgent} />
            {totalPages > 1 ? (
              <Center mt="sm">
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Center>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

/**
 * Счётчик сделок во вкладке — как «Документы N» в карточке сделки: серое количество, только когда оно известно и не
 * ноль. Во время поиска счётчик скрыт: он считает все сделки стороны, а список показывает только найденные.
 */
function TabCount({ count }: { readonly count?: number }) {
  return count ? (
    <Badge size="sm" variant="light" color="gray">
      {count}
    </Badge>
  ) : null;
}
